import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import {
  useGetAuthConfigQuery,
  useGetLegalDocumentsQuery,
  useRegisterMutation,
  useSignupMutation,
  useGoogleCallbackMutation,
  useVerifyEmailOtpMutation,
  useResendEmailOtpMutation,
  type TokenData,
} from '@/redux/internal-services/onboardingApi';
import { setSession, clearSession } from '@/redux/slices/sessionStateSlice';
import { getErrorDetail } from '@/utils/apiError';
import type { RootState } from '@/redux/common/store';
import { useOnboardingRoute } from '@/hooks';
import AuthMarketingPanel from '@/components/Auth/AuthMarketingPanel';
import OtpCodeInput from '@/components/UI/OtpCodeInput';
import logoWn from '@/assets/logo-wn-sc.png';

type Step = 'signin' | 'otp' | 'google_tos';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.sessionState.isAuthenticated);
  const tenantId = useSelector((state: RootState) => state.sessionState.tenantId);
  const userEmail = useSelector((state: RootState) => state.sessionState.userEmail);

  const { target: onboardingTarget, isLoading: onboardingLoading } = useOnboardingRoute();

  React.useEffect(() => {
    if (!(isAuthenticated && tenantId)) return;
    // Defer until the onboarding state resolves, then route to the stage the user belongs on
    // (wizard / settings) — otherwise hand off to RootRedirect.
    if (onboardingLoading) return;
    navigate(onboardingTarget ?? '/', { replace: true });
  }, [isAuthenticated, tenantId, navigate, onboardingTarget, onboardingLoading]);

  const { data: authConfigData } = useGetAuthConfigQuery();
  const { data: legalDocsData } = useGetLegalDocumentsQuery();
  const legalDocs = legalDocsData?.data ?? [];

  const openLegalDoc = (key: string) => {
    const doc = legalDocs.find((d) => d.document_key === key);
    if (!doc) return;
    const blob = new Blob([doc.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const [register, { isLoading: isRegistering }] = useRegisterMutation();
  const [signupMutation, { isLoading: isSigningUp }] = useSignupMutation();
  const [googleCallback] = useGoogleCallbackMutation();
  const [verifyEmailOtp, { isLoading: isVerifyingOtp }] = useVerifyEmailOtpMutation();
  const [resendEmailOtp, { isLoading: isResending }] = useResendEmailOtpMutation();
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendBlocked, setResendBlocked] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const enabledMethods = authConfigData?.data?.methods ?? ['google'];
  const showNative = enabledMethods.includes('native');
  const showGoogle = enabledMethods.includes('google');

  // GIS renders a fixed-width iframe; measure the wrapper and pass a px width (capped at GIS's
  // 400px max) so the button never overflows on narrow screens.
  const googleWrapRef = useRef<HTMLDivElement>(null);
  const [googleWidth, setGoogleWidth] = useState(360);
  useEffect(() => {
    const el = googleWrapRef.current;
    if (!el) return;
    const update = () => setGoogleWidth(Math.min(400, Math.max(200, Math.floor(el.clientWidth))));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showGoogle]);

  const [step, setStep] = useState<Step>('signin');

  const [nativeFullName, setNativeFullName] = useState('');
  const [nativeEmail, setNativeEmail] = useState('');
  const [nativePassword, setNativePassword] = useState('');
  const [nativeConfirmPassword, setNativeConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [authError, setAuthError] = useState('');

  const [otpCode, setOtpCode] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [signingInMessage, setSigningInMessage] = useState('');
  const [googleTosData, setGoogleTosData] = useState<{ name: string; email: string } | null>(null);
  const [googleTosAccepted, setGoogleTosAccepted] = useState(false);
  const [googleTosError, setGoogleTosError] = useState('');
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);

  const inputClass = 'w-full border border-ui-border rounded-lg px-3 py-2.5 pr-10 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent';

  const _dispatchSession = (data: TokenData) => {
    dispatch(setSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      user: {
        user_id: data.user_id,
        email: data.email,
        name: data.name ?? data.email.split('@')[0],
        roles: data.roles,
        role_names: data.role_names,
        permissions: data.permissions,
        tenant_id: data.tenant_id ?? '',
        subscription_status: data.subscription_status,
        onboarding_completed: data.onboarding_completed,
        sam_welcome_shown: data.sam_welcome_shown,
      },
    }));
  };

  const handleGoogleSuccess = async (credential: string) => {
    setAuthError('');
    try {
      const result = await googleCallback({ id_token: credential }).unwrap();
      _dispatchSession(result.data);
      if (result.data.tenant_id) {
        setSigningInMessage('You already have an account. Signing you in…');
        return;
      }
      if (!result.data.tenant_id) {
        if (result.data.email_verified) {
          // Google already verified the email — gate behind ToS acceptance before creating workspace.
          setGoogleTosData({ name: result.data.name || result.data.email.split('@')[0], email: result.data.email });
          setStep('google_tos');
        } else {
          setOtpEmail(result.data.email);
          setStep('otp');
          setResendCooldown(60);
        }
      }
    } catch {
      setAuthError(t('auth.googleFailed', 'Google sign-in failed.'));
    }
  };

  const handleGoogleTosConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!googleTosAccepted || !googleTosData) return;
    setGoogleTosError('');
    setIsCreatingWorkspace(true);
    try {
      const legalAcceptances = legalDocs.map((doc) => ({ document_key: doc.document_key, version: doc.version }));
      const regResult = await register({
        company_name: '',
        primary_contact: { name: googleTosData.name, email: googleTosData.email },
        legal_acceptances: legalAcceptances,
        confirm_duplicate: false,
      }).unwrap();
      _dispatchSession(regResult.data as unknown as TokenData);
    } catch (err: unknown) {
      setGoogleTosError(getErrorDetail(err) ?? 'Failed to create workspace. Please try again.');
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  const handleNativeSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    if (nativePassword !== nativeConfirmPassword) {
      setAuthError(t('auth.passwordMismatch', 'Passwords do not match.'));
      return;
    }
    try {
      const result = await signupMutation({ email: nativeEmail, password: nativePassword, name: nativeFullName }).unwrap();
      _dispatchSession(result.data);
      if (!result.data.email_verified) {
        setOtpEmail(result.data.email);
        setStep('otp');
        setResendCooldown(60);
      } else {
        setOtpEmail(result.data.email);
        setStep('otp');
        setResendCooldown(60);
      }
    } catch (err: unknown) {
      setAuthError(getErrorDetail(err) ?? t('auth.signupFailed', 'Account creation failed.'));
    }
  };

  const handleVerifyAndRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    try {
      await verifyEmailOtp({ email: otpEmail, otp: otpCode }).unwrap();
    } catch {
      setOtpError('Invalid or expired code. Please try again.');
      return;
    }

    try {
      const email = otpEmail || userEmail || '';
      const legalAcceptances = legalAccepted
        ? legalDocs.map((doc) => ({ document_key: doc.document_key, version: doc.version }))
        : [];
      const result = await register({
        company_name: '',
        primary_contact: { name: nativeFullName || email.split('@')[0], email },
        legal_acceptances: legalAcceptances,
        confirm_duplicate: false,
      }).unwrap();

      if (!result.success) {
        setOtpError('Workspace creation failed. Please try again.');
        return;
      }

      const tokenData = result.data as unknown as TokenData;
      _dispatchSession(tokenData);
    } catch (err: unknown) {
      setOtpError(getErrorDetail(err) ?? 'Failed to create workspace.');
    }
  };

  const handleResendOtp = async () => {
    setOtpError('');
    try {
      await resendEmailOtp({ email: otpEmail }).unwrap();
      setResendCooldown(60);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message_code?: number } };
      if (apiErr?.data?.message_code === 3306) {
        setResendBlocked(true);
        setOtpError(t('messages.3306'));
      } else {
        setOtpError('Failed to resend code.');
        setResendCooldown(60);
      }
    }
  };

  return (
    <div className="force-light flex min-h-screen bg-ui">
      <AuthMarketingPanel
        heading="Set up your business in minutes"
        subtext="SAM is ready to handle assessments, proposals, scheduling, and invoicing from the moment you register."
        bullets={[
          '14-day free trial, no credit card required',
          'Cancel or change plans anytime',
          'AI onboarding walks you through every step',
          'Invite your team and assign roles in minutes',
          'Mobile-ready for office and field teams',
          'Get reminders, follow-ups, and updates without the admin overload',
        ]}
      />

      <div className={`flex-1 flex ${step === 'otp' ? 'items-center' : 'items-start'} justify-center bg-ui px-6 py-12 overflow-y-auto`}>
        <div className="w-full max-w-md">

          {/* Mobile branding — hidden on lg+ where AuthMarketingPanel shows */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="flex items-center gap-3 rounded-2xl px-5 py-3" style={{ background: '#5b21b6' }}>
              <img src={logoWn} alt="" className="h-9 w-auto object-contain" />
              <span className="text-lg font-bold tracking-tight text-white">Smart Contractor</span>
            </div>
            <span className="mt-2.5 text-xs text-ui-text3 tracking-wide">Built for the trades. Backed by AI.</span>
          </div>


          {step === 'signin' && (
            <>
              <h1 className="text-2xl font-bold text-[#5b21b6] mb-1">
                Create your company workspace
              </h1>
              <p className="text-ui-text2 text-sm mb-8">
                Set up your company and get started with Smart Contractor in just a few steps.
              </p>

              {signingInMessage && (
                <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800 border border-green-200">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {signingInMessage}
                </div>
              )}

              {showGoogle && (
                // Center the GIS iframe button; pass a measured px width (capped at 400) so it fits
                // the column on every screen size instead of overflowing on mobile.
                <div ref={googleWrapRef} className="mb-4 flex w-full justify-center">
                  <GoogleLogin
                    text="continue_with"
                    onSuccess={(cr) => { if (cr.credential) handleGoogleSuccess(cr.credential); }}
                    onError={() => setAuthError(t('auth.googleFailed', 'Google sign-in failed.'))}
                    width={String(googleWidth)}
                  />
                </div>
              )}

              {showGoogle && showNative && (
                <div className="flex items-center my-5">
                  <hr className="flex-1 border-ui-border" />
                  <span className="px-4 text-ui-text3 text-sm font-medium">OR</span>
                  <hr className="flex-1 border-ui-border" />
                </div>
              )}

              {showNative && (
                <form onSubmit={handleNativeSignup} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ui-text mb-1.5">Full Name</label>
                    <div className="relative">
                      <input type="text" placeholder="John Smith" value={nativeFullName}
                        onChange={(e) => setNativeFullName(e.target.value)} required className={inputClass}
                      />                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-text mb-1.5">Email address</label>
                    <div className="relative">
                      <input type="email" placeholder="john@company.com" value={nativeEmail}
                        onChange={(e) => setNativeEmail(e.target.value)} required className={inputClass}
                      />                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-text mb-1.5">Password</label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={nativePassword}
                        onChange={(e) => setNativePassword(e.target.value)} required className={inputClass}
                      />
                      <button
                        type="button"
                        onMouseDown={() => setShowPassword(true)}
                        onMouseUp={() => setShowPassword(false)}
                        onMouseLeave={() => setShowPassword(false)}
                        onTouchStart={() => setShowPassword(true)}
                        onTouchEnd={() => setShowPassword(false)}
                        className="absolute inset-y-0 right-3 flex items-center text-ui-text3 hover:text-ui-text2"
                        tabIndex={-1}
                        aria-label="Hold to show password"
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-text mb-1.5">Confirm password</label>
                    <div className="relative">
                      <input type={showConfirmPassword ? 'text' : 'password'} placeholder="Re-enter your password" value={nativeConfirmPassword}
                        onChange={(e) => setNativeConfirmPassword(e.target.value)} required className={inputClass}
                      />
                      <button
                        type="button"
                        onMouseDown={() => setShowConfirmPassword(true)}
                        onMouseUp={() => setShowConfirmPassword(false)}
                        onMouseLeave={() => setShowConfirmPassword(false)}
                        onTouchStart={() => setShowConfirmPassword(true)}
                        onTouchEnd={() => setShowConfirmPassword(false)}
                        className="absolute inset-y-0 right-3 flex items-center text-ui-text3 hover:text-ui-text2"
                        tabIndex={-1}
                        aria-label="Hold to show password"
                      >
                        {showConfirmPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer pt-1">
                    <input
                      type="checkbox"
                      checked={legalAccepted}
                      onChange={(e) => setLegalAccepted(e.target.checked)}
                      required
                      className="w-4 h-4 mt-0.5 accent-purple-600 flex-shrink-0"
                    />
                    <span className="text-sm text-ui-text2">
                      I agree to the{' '}
                      <button type="button" onClick={() => openLegalDoc('terms_of_service')}
                        className="text-[#5b21b6] font-medium hover:underline">Terms of Service</button>
                      {' '}and{' '}
                      <button type="button" onClick={() => openLegalDoc('privacy_policy')}
                        className="text-[#5b21b6] font-medium hover:underline">Privacy Policy</button>
                    </span>
                  </label>
                  {authError && <p className="text-red-500 text-sm">{authError}</p>}
                  <button type="submit" disabled={isSigningUp}
                    className="w-full bg-[#1c1849] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#2d2b6b] transition disabled:opacity-50"
                  >
                    {isSigningUp ? t('common.loading') : 'Continue'}
                  </button>
                </form>
              )}

              {authError && !showNative && <p className="text-red-500 text-sm mt-3">{authError}</p>}

              <p className="mt-6 text-center text-sm text-ui-text2">
                Already have an account?{' '}
                <button onClick={() => { dispatch(clearSession()); navigate('/login'); }} className="text-[#5b21b6] hover:underline font-medium">
                  Sign in
                </button>
              </p>
            </>
          )}

          {step === 'otp' && (
            <div className="bg-ui-card rounded-2xl shadow-md px-10 py-10 text-center">
              <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-brand-dark" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-ui-text mb-2">Verify Your Account</h2>
              <p className="text-ui-text2 text-sm mb-1">We sent a verification code to:</p>
              <p className="font-semibold text-ui-text text-sm mb-6">{otpEmail}</p>

              <form onSubmit={handleVerifyAndRegister}>
                <p className="text-sm font-medium text-ui-text text-left mb-3">Enter 6-Digit Code</p>
                <div className="mb-6">
                  <OtpCodeInput value={otpCode} onChange={setOtpCode} autoFocus />
                </div>

                {otpError && <p className="text-red-500 text-sm mb-4">{otpError}</p>}

                <button
                  type="submit"
                  disabled={isVerifyingOtp || isRegistering || otpCode.length !== 6}
                  className="w-full bg-[#1c1849] text-white py-3 rounded-lg font-semibold text-sm hover:bg-[#2d2b6b] transition disabled:opacity-50"
                >
                  {(isVerifyingOtp || isRegistering) ? t('common.loading') : 'Continue'}
                </button>
              </form>

              <p className="mt-5 text-sm text-ui-text2">
                Didn&apos;t receive the code?{' '}
                {resendBlocked ? (
                  <span className="text-red-500 font-medium">{t('messages.3306')}</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isResending || resendCooldown > 0}
                    className="text-brand-dark font-medium hover:underline disabled:opacity-50"
                  >
                    {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                  </button>
                )}
              </p>
            </div>
          )}

          {step === 'google_tos' && googleTosData && (
            <div className="bg-ui-card rounded-2xl shadow-md px-5 py-8 text-center sm:px-10 sm:py-10">
              <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-brand-dark" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-[#272727] mb-1">Almost there!</h2>
              <p className="text-ui-text2 text-sm mb-1">Continuing as</p>
              <p className="font-semibold text-[#272727] text-sm mb-6">
                {googleTosData.name} · {googleTosData.email}
              </p>

              <form onSubmit={handleGoogleTosConfirm} className="text-left">
                <label className="flex items-start gap-2.5 cursor-pointer mb-6">
                  <input
                    type="checkbox"
                    checked={googleTosAccepted}
                    onChange={(e) => setGoogleTosAccepted(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-purple-600 flex-shrink-0"
                  />
                  <span className="text-sm text-ui-text2">
                    I agree to the{' '}
                    <button type="button" onClick={() => openLegalDoc('terms_of_service')}
                      className="text-[#5b21b6] font-medium hover:underline">Terms of Service</button>
                    {' '}and{' '}
                    <button type="button" onClick={() => openLegalDoc('privacy_policy')}
                      className="text-[#5b21b6] font-medium hover:underline">Privacy Policy</button>
                  </span>
                </label>

                {googleTosError && <p className="text-red-500 text-sm mb-4">{googleTosError}</p>}

                <button
                  type="submit"
                  disabled={!googleTosAccepted || isCreatingWorkspace}
                  className="w-full bg-[#1c1849] text-white py-3 rounded-lg font-semibold text-sm hover:bg-[#2d2b6b] transition disabled:opacity-50"
                >
                  {isCreatingWorkspace ? t('common.loading') : 'Continue'}
                </button>
              </form>

              <button
                type="button"
                onClick={() => { setStep('signin'); setGoogleTosData(null); setGoogleTosAccepted(false); setGoogleTosError(''); }}
                className="mt-4 text-sm text-ui-text2 hover:underline"
              >
                Back
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
